import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Plus,
  Trash2,
  Upload,
  ClipboardCheck,
  Target,
  BookOpen,
  Puzzle,
  Dumbbell,
  Layers,
  FlaskConical,
  Briefcase,
  StickyNote
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import VideoUrlInput from '../../components/Common/VideoUrlInput';
import MicroQuizEditor from '../../components/Courses/MicroQuizEditor';

// ─── Step metadata ────────────────────────────────────────────────────────────
const STEP_ICON_COLOR = 'text-[#1a3884] dark:text-[#4f7dff]';

const STEPS = [
  { code: '1', key: 'stepA_Why', label: '1. WHY', desc: 'Motivational hook: why this skill matters', icon: Target, type: 'video+text' },
  { code: '2', key: 'stepB_Story', label: '2. STORY', desc: 'Real-world narrative or case video', icon: BookOpen, type: 'video+text' },
  { code: '3', key: 'stepC_Framework', label: '3. FRAMEWORK', desc: 'Core concept, diagram, key points', icon: Puzzle, type: 'rich-text' },
  { code: '4', key: 'stepD_Practice', label: '4. PRACTICE', desc: 'Interactive activity or quiz', icon: Dumbbell, type: 'practice' },
  { code: '5', key: 'stepE_FlashCard', label: '5. FLASH CARDS', desc: 'Flip cards for reinforcement', icon: Layers, type: 'flashcards' },
  { code: '6', key: 'stepF_AdvancedPractice', label: '6. ADVANCED PRACTICE', desc: 'Deeper exercise / essay submission', icon: FlaskConical, type: 'reflection' },
  { code: '7', key: 'stepG_CaseStudy', label: '7. CASE STUDY', desc: 'Scenario & reflection questions', icon: Briefcase, type: 'case-study' },
  { code: '8', key: 'stepH_Notes', label: '8. SELF-REFLECTION', desc: 'Personal reflection and action plans', icon: StickyNote, type: 'notes' },
];

// ─── Rich Text Area ───────────────────────────────────────────────────────────
const RichArea = ({ label, value, onChange, rows = 4, placeholder = '' }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>
    <textarea rows={rows} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] focus:border-transparent transition-all resize-none" />
  </div>
);

// ─── FlashCard editor ─────────────────────────────────────────────────────────
const FlashCardEditor = ({ cards = [], onChange }) => {
  const add = () => onChange([...cards, { front: '', back: '', order: cards.length }]);
  const remove = (i) => onChange(cards.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const updated = [...cards];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };
  return (
    <div className="space-y-3">
      {cards.map((card, i) => (
        <div key={i} className="grid grid-cols-2 gap-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl p-3">
          <div>
            <label className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-1 block">FRONT (Question)</label>
            <textarea rows={2} value={card.front} onChange={e => update(i, 'front', e.target.value)}
              placeholder="Question or prompt…"
              className="w-full px-2 py-1.5 bg-white dark:bg-[#00152E] border border-amber-200 dark:border-amber-800/40 rounded-lg text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-amber-300 resize-none" />
          </div>
          <div className="relative">
            <label className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-1 block">BACK (Answer)</label>
            <textarea rows={2} value={card.back} onChange={e => update(i, 'back', e.target.value)}
              placeholder="Key insight or answer…"
              className="w-full px-2 py-1.5 bg-white dark:bg-[#00152E] border border-amber-200 dark:border-amber-800/40 rounded-lg text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-amber-300 resize-none" />
            <button onClick={() => remove(i)} type="button"
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2 border-2 border-dashed border-amber-300 dark:border-amber-700/50 text-amber-600 dark:text-amber-400 rounded-xl text-sm font-semibold hover:bg-amber-50 dark:hover:bg-amber-900/10 flex items-center justify-center gap-2 transition-colors">
        <Plus className="w-4 h-4" /> Add Flash Card
      </button>
    </div>
  );
};

// ─── Reflection question editor ───────────────────────────────────────────────
const ReflectionEditor = ({ questions = [], onChange, label = 'Reflection Questions' }) => {
  const add = () => onChange([...questions, { question: '', type: 'essay', wordLimit: 200, isRequired: true, sequence: questions.length + 1 }]);
  const remove = (i) => onChange(questions.filter((_, idx) => idx !== i));
  const update = (i, field, val) => { const u = [...questions]; u[i] = { ...u[i], [field]: val }; onChange(u); };
  const updateOption = (qIdx, optIdx, val) => {
    const u = [...questions];
    const opts = [...(u[qIdx].options || ['', '', '', ''])];
    opts[optIdx] = val;
    u[qIdx] = { ...u[qIdx], options: opts };
    onChange(u);
  };
  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">{label}</label>
      {questions.map((q, i) => (
        <div key={i} className="bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/40 rounded-xl p-4 space-y-3 shadow-sm">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 bg-[#1a3884] text-white rounded-lg text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
            <textarea rows={2} value={q.question || q.questionText || ''} onChange={e => update(i, 'question', e.target.value)}
              placeholder="Write your question…"
              className="flex-1 px-3 py-2 bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] resize-none" />
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 mt-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-3 ml-8">
            <select value={q.type} onChange={e => {
              const newType = e.target.value;
              const updateData = { type: newType };
              if (newType === 'mcq' && !q.options) {
                updateData.options = ['', '', '', ''];
                updateData.correctAnswer = '';
              }
              const u = [...questions];
              u[i] = { ...u[i], ...updateData };
              onChange(u);
            }}
              className="text-xs px-2 py-1 bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#1a3884] font-medium">
              <option value="essay">Essay</option>
              <option value="text">Short text</option>
              <option value="file-upload">File upload</option>
              <option value="mcq">MCQ</option>
            </select>
            {q.type !== 'mcq' && q.type !== 'file-upload' && (
              <input type="number" value={q.wordLimit || 200} onChange={e => update(i, 'wordLimit', parseInt(e.target.value))}
                placeholder="Word limit" className="w-24 text-xs px-2 py-1 bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#1a3884]" />
            )}
          </div>

          {q.type === 'mcq' && (
            <div className="ml-8 space-y-2 mt-2 border-t border-slate-200 dark:border-[#1a3884]/30 pt-3">
              <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Options & Correct Answer</label>
              {(q.options || ['', '', '', '']).map((opt, oIdx) => (
                <div key={oIdx} className="flex items-center gap-2">
                  <input type="radio" name={`reflection-correct-${i}`} checked={q.correctAnswer === opt && opt !== ''}
                    onChange={() => update(i, 'correctAnswer', opt)}
                    className="w-4 h-4 text-[#1a3884] focus:ring-[#1a3884]" />
                  <input type="text" value={opt} onChange={e => updateOption(i, oIdx, e.target.value)}
                    placeholder={`Option ${oIdx + 1}`}
                    className={`flex-1 px-3 py-1.5 border rounded-lg text-xs text-slate-900 dark:text-white focus:ring-2 focus:ring-[#1a3884] ${q.correctAnswer === opt && opt !== '' ? 'border-[#1a3884] bg-blue-50/50 dark:bg-[#1a3884]/20' : 'border-slate-200 dark:border-[#1a3884]/50 bg-white dark:bg-[#002147]'}`} />
                </div>
              ))}
              <p className="text-[10px] text-slate-400 dark:text-slate-500">Select the radio button next to the correct answer.</p>

              <div className="mt-2.5">
                <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">Explanation (Optional)</label>
                <input type="text" value={q.explanation || ''} onChange={e => update(i, 'explanation', e.target.value)}
                  placeholder="Explain why this option is correct..."
                  className="w-full px-3 py-1.5 bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884]" />
              </div>
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2 border-2 border-dashed border-[#1a3884]/30 dark:border-[#4f7dff]/40 text-[#1a3884] dark:text-[#4f7dff] rounded-xl text-sm font-semibold hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/10 flex items-center justify-center gap-2 transition-colors">
        <Plus className="w-4 h-4" /> Add Question
      </button>
    </div>
  );
};

// ─── Quiz question editor ───────────────────────────────────────────────
const QuizEditor = ({ questions = [], onChange, label = 'Quiz Questions' }) => {
  const add = () => onChange([...questions, { question: '', type: 'mcq', options: ['', '', '', ''], correctAnswer: '', explanation: '' }]);
  const remove = (i) => onChange(questions.filter((_, idx) => idx !== i));
  const update = (i, field, val) => { const u = [...questions]; u[i] = { ...u[i], [field]: val }; onChange(u); };
  const updateOption = (qIdx, optIdx, val) => {
    const u = [...questions];
    const opts = [...(u[qIdx].options || [])];
    opts[optIdx] = val;
    u[qIdx] = { ...u[qIdx], options: opts };
    onChange(u);
  };

  return (
    <div className="space-y-4 mt-2">
      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">{label}</label>
      {questions.map((q, i) => (
        <div key={i} className="bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/40 shadow-sm rounded-xl p-4 space-y-4 relative">
          <div className="flex items-start gap-3">
            <span className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">Q{i + 1}</span>
            <div className="flex-1 space-y-3">
              <textarea rows={2} value={q.question} onChange={e => update(i, 'question', e.target.value)}
                placeholder="Enter the multiple-choice question…"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-emerald-400 resize-none" />

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Options & Correct Answer</label>
                {(q.options || ['', '', '', '']).map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <input type="radio" name={`correct-${i}`} checked={q.correctAnswer === opt && opt !== ''}
                      onChange={() => update(i, 'correctAnswer', opt)}
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
                    <input type="text" value={opt} onChange={e => updateOption(i, oIdx, e.target.value)}
                      placeholder={`Option ${oIdx + 1}`}
                      className={`flex-1 px-3 py-1.5 border rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-400 ${q.correctAnswer === opt && opt !== '' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-[#1a3884]/50 bg-white dark:bg-[#00152E]'}`} />
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 dark:text-slate-500">Select the radio button next to the correct answer.</p>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block">Explanation (Optional)</label>
                <textarea rows={1} value={q.explanation || ''} onChange={e => update(i, 'explanation', e.target.value)}
                  placeholder="Why is this answer correct?"
                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-lg text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-emerald-400 resize-none" />
              </div>
            </div>
            <button type="button" onClick={() => remove(i)} className="text-slate-400 dark:text-slate-500 hover:text-red-500 p-1 absolute top-3 right-3">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2.5 border-2 border-dashed border-emerald-300 dark:border-emerald-700/50 text-emerald-600 dark:text-emerald-400 rounded-xl text-sm font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-900/10 flex items-center justify-center gap-2 transition-colors">
        <Plus className="w-4 h-4" /> Add MCQ Question
      </button>
    </div>
  );
};


// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const CourseBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [flow, setFlow] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openStep, setOpenStep] = useState('1');

  useEffect(() => {
    if (id) fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/courses/${id}`);
      if (data.success) {
        setCourse(data.data);
        setFlow(data.data.learningFlow || {});
      }
    } catch (e) {
      toast.error('Failed to load course.');
    } finally {
      setLoading(false);
    }
  };

  const updateStep = (stepKey, field, value) => {
    setFlow(prev => ({
      ...prev,
      [stepKey]: { ...(prev[stepKey] || {}), [field]: value }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const practiceSubmissionType = flow.stepD_Practice?.submissionType || 'reflection';
      const updatedFlow = {
        ...flow,
        stepD_Practice: {
          ...(flow.stepD_Practice || {}),
          activityType: practiceSubmissionType === 'quiz' ? 'quiz' : 'reflection'
        },
        stepG_CaseStudy: {
          ...(flow.stepG_CaseStudy || {}),
          isEnabled: true
        },
        stepH_Notes: {
          ...(flow.stepH_Notes || {}),
          isEnabled: true
        }
      };
      const { data } = await api.put(`/courses/${id}`, { learningFlow: updatedFlow });
      if (data.success) {
        setCourse(data.data);
        setFlow(data.data.learningFlow || updatedFlow);
        toast.success('Course structure saved successfully!');
        navigate('/courses');
      } else {
        toast.error(data.message || 'Save failed.');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      try {
        setSaving(true);
        const { data } = await api.delete(`/courses/${id}`);
        if (data.success) {
          toast.success('Course deleted successfully');
          navigate('/courses');
        } else {
          toast.error(data.message || 'Failed to delete course');
        }
      } catch (e) {
        toast.error('Failed to delete course');
      } finally {
        setSaving(false);
      }
    }
  };

  const getStepCompletion = (stepKey) => {
    const s = flow[stepKey];
    if (!s) return false;
    if (stepKey === 'stepA_Why' || stepKey === 'stepB_Story') return !!(s.videoUrl || s.text || s.narrative);
    if (stepKey === 'stepC_Framework') return !!(s.content || s.keyPoints?.length);
    if (stepKey === 'stepD_Practice') {
      if ((s.submissionType || 'reflection') === 'quiz') return !!(s.questions?.length);
      return !!(s.instructions);
    }
    if (stepKey === 'stepE_FlashCard') return !!(s.cards?.length);
    if (stepKey === 'stepF_AdvancedPractice') return !!(s.instructions || s.questions?.length);
    if (stepKey === 'stepG_CaseStudy') return !!(s.caseTitle || s.caseText || s.questions?.length);
    if (stepKey === 'stepH_Notes') return !!(s.prompt || s.videoUrl);
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#00152E] dark:to-[#002147]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3884] dark:border-[#4f7dff]"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#00152E] dark:to-[#002147]">
        <p className="text-slate-500 dark:text-slate-400">Course not found</p>
      </div>
    );
  }

  const completedCount = STEPS.filter(s => getStepCompletion(s.key)).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#00152E] dark:to-[#002147] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header Section */}
        <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-slate-200 dark:border-[#1a3884]/40 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/courses')}
                className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-[#4f7dff] hover:bg-blue-50 dark:hover:bg-[#1a3884]/40 hover:border-[#1a3884] dark:hover:border-[#4f7dff] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{course.title}</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Course Code: {course.courseCode}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => navigate(`/courses/${id}/assessment-builder`)}
                className="inline-flex items-center px-5 py-2.5 bg-white dark:bg-[#00152E] text-[#1a3884] dark:text-[#4f7dff] border-2 border-[#1a3884]/30 dark:border-[#4f7dff]/40 rounded-xl font-bold text-sm hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/20 hover:border-[#1a3884] dark:hover:border-[#4f7dff] transition-all gap-1.5">
                <ClipboardCheck className="w-4 h-4" />
                Gate Assessment (MCQ/FIB/SJT)
              </button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center px-5 py-2.5 bg-[#1a3884] hover:bg-[#142c68] text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg hover:shadow-[#1a3884]/25 transition-all disabled:opacity-60 gap-1.5">
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={handleDelete} disabled={saving}
                className="inline-flex items-center px-5 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50 rounded-xl font-bold text-sm hover:bg-red-600 hover:text-white dark:hover:bg-red-600 dark:hover:text-white transition-all disabled:opacity-60 gap-1.5">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Progress Header */}
        <div className="bg-white dark:bg-[#002147] rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-[#1a3884]/40">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[11px] font-semibold text-[#1a3884]/60 dark:text-[#4f7dff]/70 tracking-widest uppercase mb-1">Course Structure · 8-Stage Flow</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm">{course?.courseNumber} · {course?.stage?.replace('_', ' ')}</p>
            </div>
            <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{completedCount} of 8 steps complete</span>
          </div>
          <div className="mt-4 h-1.5 bg-slate-100 dark:bg-[#00152E] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#1a3884] to-[#4f7dff] rounded-full transition-all duration-500" style={{ width: `${(completedCount / 8) * 100}%` }} />
          </div>
          <div className="flex gap-2 mt-5 flex-wrap">
            {STEPS.map(s => {
              const sDone = getStepCompletion(s.key);
              const sActive = openStep === s.code;
              return (
                <button key={s.code} type="button"
                  onClick={() => setOpenStep(s.code)}
                  title={s.label.replace(/^\d+\.\s*/, '')}
                  className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-all border
                    ${sActive ? 'bg-[#1a3884] text-white border-[#1a3884] shadow-sm scale-105' : sDone ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/50 hover:scale-105' : 'bg-white dark:bg-[#00152E] text-slate-400 dark:text-slate-500 border-slate-200 dark:border-[#1a3884]/50 hover:border-[#1a3884]/40 dark:hover:border-[#4f7dff]/40'}`}>
                  {sDone && !sActive ? <CheckCircle className="w-4 h-4" /> : s.code}
                </button>
              );
            })}
          </div>
        </div>

        {/* 8-Stage Accordion */}
        <div className="space-y-4">
          {STEPS.map(step => {
            const isOpen = openStep === step.code;
            const done = getStepCompletion(step.key);
            const stepData = flow[step.key] || {};
            const StepIcon = step.icon;

            return (
              <div key={step.code} className={`bg-white dark:bg-[#002147] rounded-2xl border shadow-[0_2px_10px_-3px_rgba(26,56,132,0.1)] dark:shadow-blue-900/10 overflow-hidden transition-all hover:shadow-[0_4px_16px_-3px_rgba(26,56,132,0.14)]
                ${isOpen ? 'border-[#1a3884]/40 dark:border-[#4f7dff]/40' : done ? 'border-emerald-200 dark:border-emerald-700/40' : 'border-slate-100 dark:border-[#1a3884]/40'}`}>

                {/* Step header */}
                <button type="button" onClick={() => setOpenStep(isOpen ? null : step.code)}
                  className="w-full flex items-center gap-4 p-6 text-left hover:bg-slate-50 dark:hover:bg-[#1a3884]/10 transition-colors">
                  <div className={`w-12 h-12 bg-white dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-all ${isOpen ? 'border-[#1a3884] dark:border-[#4f7dff] shadow-md scale-105' : ''}`}>
                    <StepIcon className={`w-5 h-5 ${STEP_ICON_COLOR}`} strokeWidth={2.25} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-bold text-slate-900 dark:text-white text-lg">{step.label}</p>
                      {step.optional && <span className="text-[10px] bg-slate-100 dark:bg-[#1a3884]/30 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full font-bold uppercase">Optional</span>}
                      {done && <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{step.desc}</p>
                  </div>
                  {isOpen ? <ChevronUp className="w-6 h-6 text-slate-400 dark:text-slate-500" /> : <ChevronDown className="w-6 h-6 text-slate-400 dark:text-slate-500" />}
                </button>

                {/* Step content */}
                {isOpen && (
                  <div className="px-6 pb-6 pt-2 space-y-5 border-t border-slate-100 dark:border-[#1a3884]/30">

                    {/* 1. WHY */}
                    {step.code === '1' && (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Title</label>
                          <input type="text" value={stepData.title || ''} onChange={e => updateStep(step.key, 'title', e.target.value)}
                            placeholder="e.g. Why this skill is essential"
                            className="w-full px-3 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] transition-all" />
                        </div>
                        <VideoUrlInput label="Why Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} />
                        <RichArea label="Description" value={stepData.text} onChange={v => updateStep(step.key, 'text', v)} placeholder="Write the 'why' context — what problem does this skill solve?" />
                      </>
                    )}

                    {/* 2. STORY */}
                    {step.code === '2' && (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Title</label>
                          <input type="text" value={stepData.title || ''} onChange={e => updateStep(step.key, 'title', e.target.value)}
                            placeholder="e.g. The Story of Transformation"
                            className="w-full px-3 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] transition-all" />
                        </div>
                        <VideoUrlInput label="Story Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} />
                        <RichArea label="Description" value={stepData.narrative} onChange={v => updateStep(step.key, 'narrative', v)} placeholder="Tell a real-world story that illustrates this skill…" rows={5} />
                      </>
                    )}

                    {/* 3. FRAMEWORK */}
                    {step.code === '3' && (
                      <>
                        <VideoUrlInput label="Framework Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} />

                        {/* DIAGRAM UPLOAD */}
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Framework Image / Diagram</label>
                          {stepData.diagramUrl ? (
                            <div className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-[#1a3884]/50 max-w-md">
                              <img
                                src={stepData.diagramUrl}
                                alt="Framework Diagram"
                                className="w-full h-48 object-contain bg-slate-50 dark:bg-[#00152E]"
                              />
                              <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                <label className="cursor-pointer px-4 py-2 bg-white text-slate-900 rounded-lg text-sm font-bold shadow-lg hover:scale-105 transition-transform">
                                  Change Image
                                  <input type="file" accept="image/*" onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('image', file);
                                    const loadToast = toast.loading('Uploading diagram...');
                                    try {
                                      const { data } = await api.post('/upload/image', formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                      });
                                      if (data.success) {
                                        updateStep(step.key, 'diagramUrl', data.data.url);
                                        toast.success('Diagram uploaded successfully!', { id: loadToast });
                                      } else {
                                        toast.error('Upload failed.', { id: loadToast });
                                      }
                                    } catch (err) {
                                      toast.error('Upload failed.', { id: loadToast });
                                    }
                                  }} className="hidden" />
                                </label>
                                <button type="button" onClick={() => updateStep(step.key, 'diagramUrl', '')}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold shadow-lg hover:scale-105 transition-transform">
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center w-full max-w-md h-32 border-2 border-slate-200 dark:border-[#1a3884]/50 border-dashed rounded-xl cursor-pointer bg-slate-50 dark:bg-[#00152E] hover:bg-slate-100 dark:hover:bg-[#1a3884]/20 hover:border-slate-300 dark:hover:border-[#1a3884] transition-all">
                              <div className="flex flex-col items-center justify-center pt-4 pb-4">
                                <Upload className="w-6 h-6 text-slate-400 dark:text-slate-500 mb-1" />
                                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Click to upload framework diagram</p>
                                <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">PNG, JPG, SVG up to 5MB</p>
                              </div>
                              <input type="file" accept="image/*" onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('image', file);
                                const loadToast = toast.loading('Uploading diagram...');
                                try {
                                  const { data } = await api.post('/upload/image', formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' },
                                  });
                                  if (data.success) {
                                    updateStep(step.key, 'diagramUrl', data.data.url);
                                    toast.success('Diagram uploaded successfully!', { id: loadToast });
                                  } else {
                                    toast.error('Upload failed.', { id: loadToast });
                                  }
                                } catch (err) {
                                  toast.error('Upload failed.', { id: loadToast });
                                }
                              }} className="hidden" />
                            </label>
                          )}
                        </div>

                        <RichArea label="Framework / Core Concept" value={stepData.content} onChange={v => updateStep(step.key, 'content', v)} placeholder="Explain the framework, model, or core concept…" rows={6} />
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Key Points</label>
                          {(stepData.keyPoints || []).map((pt, i) => (
                            <div key={i} className="flex gap-2 mb-2">
                              <input type="text" value={pt} onChange={e => {
                                const kp = [...(stepData.keyPoints || [])]; kp[i] = e.target.value;
                                updateStep(step.key, 'keyPoints', kp);
                              }} placeholder={`Key point ${i + 1}`}
                                className="flex-1 px-3 py-2 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147]" />
                              <button type="button" onClick={() => {
                                const kp = (stepData.keyPoints || []).filter((_, idx) => idx !== i);
                                updateStep(step.key, 'keyPoints', kp);
                              }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button type="button" onClick={() => updateStep(step.key, 'keyPoints', [...(stepData.keyPoints || []), ''])}
                            className="text-sm text-[#1a3884] dark:text-[#4f7dff] font-semibold flex items-center gap-1 hover:text-[#00152E] dark:hover:text-white mt-2">
                            <Plus className="w-4 h-4" /> Add key point
                          </button>
                        </div>
                      </>
                    )}

                    {/* 4. PRACTICE */}
                    {step.code === '4' && (
                      <>
                        <RichArea label="Instructions" value={stepData.instructions} onChange={v => updateStep(step.key, 'instructions', v)} placeholder="Practice exercise instructions…" />

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Submission Type</label>
                          <div className="flex gap-3 flex-wrap mb-4">
                            {['essay', 'file-upload', 'reflection', 'quiz'].map(t => (
                              <button key={t} type="button" onClick={() => {
                                updateStep(step.key, 'submissionType', t);
                                updateStep(step.key, 'activityType', t === 'quiz' ? 'quiz' : 'reflection');
                              }}
                                className={`px-4 py-2 rounded-xl text-sm font-bold capitalize border-2 transition-all
                                  ${(stepData.submissionType || 'reflection') === t ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'border-slate-200 dark:border-[#1a3884]/50 text-slate-600 dark:text-slate-300 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-slate-50 dark:hover:bg-[#1a3884]/10'}`}>
                                {t.replace('-', ' ')}
                              </button>
                            ))}
                          </div>
                        </div>
                        {(stepData.submissionType || 'reflection') === 'quiz' ? (
                          <MicroQuizEditor
                            questions={stepData.questions || []}
                            onChange={(q) => updateStep(step.key, 'questions', q)}
                            randomizeQuestions={stepData.randomizeQuestions !== false}
                            onRandomizeChange={(v) => updateStep(step.key, 'randomizeQuestions', v)}
                          />
                        ) : (
                          <ReflectionEditor
                            questions={stepData.questions || []}
                            onChange={q => updateStep(step.key, 'questions', q)}
                            label="Submission Details / Prompts"
                          />
                        )}
                      </>
                    )}

                    {/* 5. FLASH CARDS */}
                    {step.code === '5' && (
                      <FlashCardEditor cards={stepData.cards || []} onChange={cards => updateStep(step.key, 'cards', cards)} />
                    )}

                    {/* 6. ADVANCED PRACTICE */}
                    {step.code === '6' && (
                      <>
                        <RichArea label="Instructions" value={stepData.instructions} onChange={v => updateStep(step.key, 'instructions', v)} placeholder="Advanced exercise instructions…" />

                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Submission Type</label>
                          <div className="flex gap-3 flex-wrap mb-4">
                            {['essay', 'file-upload', 'reflection', 'quiz'].map(t => (
                              <button key={t} type="button" onClick={() => updateStep(step.key, 'submissionType', t)}
                                className={`px-4 py-2 rounded-xl text-sm font-bold capitalize border-2 transition-all
                                  ${(stepData.submissionType || 'reflection') === t ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' : 'border-slate-200 dark:border-[#1a3884]/50 text-slate-600 dark:text-slate-300 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-slate-50 dark:hover:bg-[#1a3884]/10'}`}>
                                {t.replace('-', ' ')}
                              </button>
                            ))}
                          </div>
                        </div>

                        {(stepData.submissionType || 'reflection') === 'quiz' ? (
                          <QuizEditor questions={stepData.questions || []} onChange={q => updateStep(step.key, 'questions', q)} label="Advanced Quiz Questions" />
                        ) : (
                          <ReflectionEditor questions={stepData.questions || []} onChange={q => updateStep(step.key, 'questions', q)} label="Submission Details / Prompts" />
                        )}
                      </>
                    )}

                    {/* 7. CASE STUDY */}
                    {step.code === '7' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Case Title</label>
                          <input type="text" value={stepData.caseTitle || ''} onChange={e => updateStep(step.key, 'caseTitle', e.target.value)}
                            placeholder="Enter Case Title..."
                            className="w-full px-3 py-2.5 bg-slate-50 dark:bg-[#00152E] border border-slate-200 dark:border-[#1a3884]/50 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-[#1a3884] focus:bg-white dark:focus:bg-[#002147] transition-all" />
                        </div>
                        <RichArea label="Case Narrative / Scenario" value={stepData.caseText} onChange={v => updateStep(step.key, 'caseText', v)} placeholder="Write the detailed case study scenario here..." rows={6} />
                        <ReflectionEditor questions={stepData.questions || []} onChange={q => updateStep(step.key, 'questions', q)} label="Case Study Reflection Questions" />
                      </div>
                    )}

                    {/* 8. SELF-REFLECTION */}
                    {step.code === '8' && (
                      <div className="space-y-4">
                        <VideoUrlInput label="Self-Reflection Video URL" value={stepData.videoUrl} onChange={v => updateStep(step.key, 'videoUrl', v)} />
                        <RichArea label="Self-Reflection Prompt" value={stepData.prompt} onChange={v => updateStep(step.key, 'prompt', v)} placeholder="Write the prompt for students to perform their self-reflection and action plans..." />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Save Action */}
        <div className="pt-4 pb-8">
          <button onClick={handleSave} disabled={saving}
            className="w-full py-3.5 bg-[#1a3884] hover:bg-[#142c68] text-white rounded-xl font-semibold text-sm shadow-sm hover:shadow-md hover:shadow-[#1a3884]/20 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save Course Structure</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CourseBuilder;
